import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useCreateTimeEntry, useUpdateTimeEntry } from "@/queries/use-time-tracking.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiTimeEntry } from "@/types/time-tracking.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Modal,
  ModalProps,
  Select,
  Stack,
  Textarea,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { format } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const hourOptions = Array.from({ length: 24 }, (_, i) => ({
  id: String(i).padStart(2, "0"),
  title: String(i).padStart(2, "0"),
}));

const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
  id: String(i).padStart(2, "0"),
  title: String(i).padStart(2, "0"),
}));

export interface TimeEntryEditModalProps extends ModalProps {
  entry: ApiTimeEntry | null;
  employeeId?: number;
  date?: Date;
  onSaved?: () => void;
}

export const TimeEntryEditModal = ({
  entry,
  employeeId,
  date,
  onSaved,
  ...props
}: TimeEntryEditModalProps) => {
  const { t } = useTranslation();
  const { close } = useModals();
  const { onError } = useErrorHandle();
  const createEntry = useCreateTimeEntry();
  const updateEntry = useUpdateTimeEntry();

  const schema = z.object({
    date: z.date({ error: t("errors:field_required") }),
    datetime_clocked_in: z.date({ error: t("errors:field_required") }),
    datetime_clocked_out: z.date().nullable().default(null),
    clock_in_comment: z.string().nullable().default(""),
    clock_out_comment: z.string().nullable().default(""),
    edit_reason: z.string().nullable().default(""),
  });

  const isEditing = entry !== null;

  const {
    control,
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: isEditing
      ? {
          date: entry.datetime_clocked_in,
          datetime_clocked_in: entry.datetime_clocked_in,
          datetime_clocked_out: entry.datetime_clocked_out ?? null,
          clock_in_comment: entry.clock_in_comment ?? "",
          clock_out_comment: entry.clock_out_comment ?? "",
          edit_reason: entry.edit_reason ?? "",
        }
      : {
          date: date ?? new Date(),
          datetime_clocked_in: undefined,
          datetime_clocked_out: null,
          clock_in_comment: "",
          clock_out_comment: "",
          edit_reason: "",
        },
  });

  const watchedDate = watch("date");
  const baseDate = isEditing
    ? entry.datetime_clocked_in
    : watchedDate instanceof Date
      ? watchedDate
      : (date ?? new Date());

  const isPending = isEditing ? updateEntry.isPending : createEntry.isPending;

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isEditing) {
        await updateEntry.mutateAsync({
          entryId: entry.id,
          data: {
            datetime_clocked_in: data.datetime_clocked_in,
            datetime_clocked_out: data.datetime_clocked_out,
            clock_in_comment: data.clock_in_comment || null,
            clock_out_comment: data.clock_out_comment || null,
            edit_reason: data.edit_reason || null,
          },
        });
        analyticsService.trackEvent(analyticEvents.timeTracking.entryEdited);
      } else {
        await createEntry.mutateAsync({
          employeeId: employeeId!,
          data: {
            datetime_clocked_in: data.datetime_clocked_in,
            datetime_clocked_out: data.datetime_clocked_out,
            clock_in_comment: data.clock_in_comment || null,
            clock_out_comment: data.clock_out_comment || null,
          },
        });
        analyticsService.trackEvent(analyticEvents.timeTracking.entryCreated);
      }
      close(props.id);
      onSaved?.();
    } catch (e) {
      onError(e as Error, control);
    }
  });

  return (
    <Modal
      {...props}
      title={isEditing ? t("clocking:edit_entry") : t("clocking:create_entry")}
      caption={format(baseDate, "MMM d, yyyy")}
      withCloser={true}
      closeByEscEnabled={true}
      size={"lg"}
      variant={"middle"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            onClick={onSubmit}
            isLoading={isPending}
          >
            {isEditing ? t("common:save") : t("common:create")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          {!isEditing && (
            <Controller
              control={control}
              name="date"
              render={({ field: { value, onChange } }) => (
                <Datepicker
                  value={value instanceof Date ? value : null}
                  onChange={(newDate) => {
                    onChange(newDate);
                    if (!newDate) return;
                    const clockIn = getValues("datetime_clocked_in");
                    if (clockIn instanceof Date) {
                      const updated = new Date(newDate);
                      updated.setHours(clockIn.getHours(), clockIn.getMinutes(), 0, 0);
                      setValue("datetime_clocked_in", updated);
                    }
                    const clockOut = getValues("datetime_clocked_out");
                    if (clockOut instanceof Date) {
                      const updated = new Date(newDate);
                      updated.setHours(clockOut.getHours(), clockOut.getMinutes(), 0, 0);
                      setValue("datetime_clocked_out", updated);
                    }
                  }}
                  label={t("clocking:date")}
                  error={errors.date?.message}
                />
              )}
            />
          )}
          <Title header={t("clocking:started")} size={"xs"} paddingBottom />
          <Controller
            control={control}
            name="datetime_clocked_in"
            render={({ field: { value, onChange } }) => (
              <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
                <Select
                  value={value instanceof Date ? String(value.getHours()).padStart(2, "0") : ""}
                  onChange={(v) => {
                    const base = value instanceof Date ? new Date(value) : new Date(baseDate);
                    base.setHours(Number(v), base.getMinutes(), 0, 0);
                    onChange(base);
                  }}
                  options={hourOptions}
                  emptyTitle={"HH"}
                  error={errors.datetime_clocked_in?.message}
                />
                <Select
                  value={value instanceof Date ? String(value.getMinutes()).padStart(2, "0") : ""}
                  onChange={(v) => {
                    const base = value instanceof Date ? new Date(value) : new Date(baseDate);
                    base.setHours(base.getHours(), Number(v), 0, 0);
                    onChange(base);
                  }}
                  options={minuteOptions}
                  emptyTitle={"MM"}
                />
              </Stack>
            )}
          />
          <Textarea {...register("clock_in_comment")} label={t("clocking:clock_in_comment")} />

          <Title header={t("clocking:finished")} size={"xs"} paddingTop paddingBottom />
          <Controller
            control={control}
            name="datetime_clocked_out"
            render={({ field: { value, onChange } }) => (
              <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
                <Select
                  value={value instanceof Date ? String(value.getHours()).padStart(2, "0") : ""}
                  onChange={(v) => {
                    if (v === null) {
                      onChange(null);
                      return;
                    }
                    const base = value instanceof Date ? new Date(value) : new Date(baseDate);
                    base.setHours(Number(v), base.getMinutes(), 0, 0);
                    onChange(base);
                  }}
                  options={hourOptions}
                  emptyTitle={"HH"}
                  isClearable={true}
                />
                <Select
                  value={value instanceof Date ? String(value.getMinutes()).padStart(2, "0") : ""}
                  onChange={(v) => {
                    if (v === null) {
                      onChange(null);
                      return;
                    }
                    const base = value instanceof Date ? new Date(value) : new Date(baseDate);
                    base.setHours(base.getHours(), Number(v), 0, 0);
                    onChange(base);
                  }}
                  options={minuteOptions}
                  emptyTitle={"MM"}
                  isClearable={true}
                />
              </Stack>
            )}
          />

          <Textarea {...register("clock_out_comment")} label={t("clocking:clock_out_comment")} />

          {isEditing && (
            <>
              <Title header={t("clocking:edit_reason")} size={"xs"} paddingTop paddingBottom />
              <Textarea
                {...register("edit_reason")}
                label={t("clocking:edit_reason_placeholder")}
              />
            </>
          )}
        </Stack>
      </form>
    </Modal>
  );
};
